import { useEffect, useRef } from 'react';

// Offer/job socket events are broadcast to every connected client. Without
// scoping, every open office dashboard reloads its full job list at the same
// moment ("reload herd"), which stalls the API for everyone. Coalesce bursts
// into one refresh and spread offices out a little.
const REFRESH_COALESCE_MIN_MS = 250;
const REFRESH_COALESCE_JITTER_MS = 1250;

/**
 * Subscribes to legacy socket events and triggers a refresh when relevant
 * events occur. Uses the global TempStars.socket if available.
 *
 * CRITICAL: Includes user type validation to prevent hygienist tokens
 * from triggering dentist API calls (security measure).
 *
 * When `isOwnJob` is supplied, an event that names a job this office has not
 * loaded is ignored (as the legacy home.js did). Events without a job id still
 * refresh.
 */
export const useJobSocketEvents = (
  onRefresh: () => void,
  isOwnJob?: (jobId: number) => boolean,
) => {
  const isOwnJobRef = useRef(isOwnJob);
  isOwnJobRef.current = isOwnJob;

  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleRefresh = () => {
      if (refreshTimer) return; // one refresh already pending for this burst
      const delay = REFRESH_COALESCE_MIN_MS + Math.floor(Math.random() * REFRESH_COALESCE_JITTER_MS);
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        onRefresh();
      }, delay);
    };

    const handler = (socketData?: any) => {
      try {
        // CRITICAL: Only process socket events if current user is a dentist
        // This prevents hygienist tokens from triggering dentist API calls
        const user = (window as any)?.TempStars?.User?.getCurrentUser?.();
        if (!user || !user.dentistId) {
          return; // Not a dentist, ignore this event
        }

        const jobId = Number(socketData?.jobId);
        const ownJob = isOwnJobRef.current;
        if (ownJob && Number.isFinite(jobId) && jobId > 0 && !ownJob(jobId)) {
          return; // Another office's job
        }

        scheduleRefresh();
      } catch (e) {
        // no-op
      }
    };

    let boundSocket: any = null;

    const events = [
      'newPartialOfferSubmitted',
      'cancelledPartialOffer',
      'updatedPartialOffer',
      'jobUpdated',
      'jobCancelled',
      'instabookOffer',
    ];

    const unbindFrom = (sock: any) => {
      if (!sock || typeof sock.off !== 'function') return;
      events.forEach((evt) => sock.off(evt, handler));
    };

    const bindToCurrentSocket = () => {
      const sock = (window as any)?.TempStars?.socket;
      if (!sock || typeof sock.on !== 'function' || sock === boundSocket) {
        return;
      }

      unbindFrom(boundSocket);
      events.forEach((evt) => sock.on(evt, handler));
      boundSocket = sock;
    };

    bindToCurrentSocket();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        bindToCurrentSocket();
      }
    };

    document.addEventListener('tempstars:socket-ready', bindToCurrentSocket);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      document.removeEventListener('tempstars:socket-ready', bindToCurrentSocket);
      document.removeEventListener('visibilitychange', onVisible);
      unbindFrom(boundSocket);
    };
  }, [onRefresh]);
};
